// src/features/locker-assignments/components/forms/ZonesSection.tsx
import React from 'react';
import {
  Box,
  Typography,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  FormHelperText,
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { MyFormSection } from '@/components';
import type { Zone } from '../../types';

interface ZonesSectionProps {
  zones: Zone[];
  selectedZones: string[];
  loadingHotelData: boolean;
  onZoneChange: (event: any) => void;
}

export const ZonesSection: React.FC<ZonesSectionProps> = ({
  zones,
  selectedZones,
  loadingHotelData,
  onZoneChange,
}) => {
  const translate = useTranslate();

  if (loadingHotelData) {
    return (
      <MyFormSection title={translate('resources.locker-assignments.messages.zones_title')} icon={<LocationIcon />}>
        <Typography variant="body2" color="text.secondary">
          {translate('resources.locker-assignments.messages.loading_zones')}
        </Typography>
      </MyFormSection>
    );
  }

  if (zones.length === 0) {
    return (
      <MyFormSection title={translate('resources.locker-assignments.messages.zones_title')} icon={<LocationIcon />}>
        <Alert severity="info">
          {translate('resources.locker-assignments.messages.no_zones_configured')}
        </Alert>
      </MyFormSection>
    );
  }

  return (
    <MyFormSection title={translate('resources.locker-assignments.messages.zones_title')} icon={<LocationIcon />}>
      <FormControl fullWidth size="small">
        <InputLabel id="zones-select-label">{translate('resources.locker-assignments.messages.select_zones_label')}</InputLabel>
        <Select
          labelId="zones-select-label"
          multiple
          value={selectedZones}
          onChange={onZoneChange}
          label={translate('resources.locker-assignments.messages.select_zones_label')}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as string[]).map((value) => {
                const zone = zones.find(z => z.id === value);
                return (
                  <Chip 
                    key={value} 
                    label={zone?.name || value} 
                    size="small"
                  />
                );
              })}
            </Box>
          )}
        >
          {zones.map((zone) => (
            <MenuItem key={zone.id} value={zone.id}>
              {zone.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedZones.length > 0 && (
        <FormHelperText>
          {translate('resources.locker-assignments.messages.zones_selected_count', { selected: selectedZones.length, total: zones.length })}
        </FormHelperText>
      )}
    </MyFormSection>
  );
};